import { type EventPayload, track } from '@side/analytics';
import { useSession } from '@side/next-auth/react';
import { useBreakpoints } from '@side/pantry';
import { useCallback } from 'react';
import { useInteractionMetrics } from 'src/modules/analytics/useInteractionMetrics';

const useGenerateEventPayload = () => {
  const breakpoint = useBreakpoints();
  const generateInteractionPayload = useInteractionMetrics();
  const { data: session } = useSession();
  const user = session?.user;

  return useCallback(() => {
    const interactionPayload = generateInteractionPayload();

    return {
      ...interactionPayload,
      userEmail: user?.email,
      userId: user?.id,
      userRoles: user?.roles ?? [],
      breakpoint,
      deviceHeight: window.screen?.availHeight,
      deviceWidth: window.screen?.availWidth,
      location: window?.location?.pathname,
      path: window?.location?.pathname,
      userAgent: window.navigator.userAgent,
      source: 'txm',
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? 'UNKNOWN',
    };
  }, [breakpoint, generateInteractionPayload, user]);
};

/**
 * Custom hook that abstracts out event types in order to call common hook
 * across multiple domains
 */
export const useTrackEvent = () => {
  const generatePayload = useGenerateEventPayload();
  return useCallback(
    (eventLabel: string, eventInfo: EventPayload & Record<string, unknown>) => {
      void track(eventLabel, {
        ...generatePayload(),
        ...eventInfo,
      });
    },
    [generatePayload],
  );
};
